import {
  WretchOptions,
  WretchResponse,
  httpGetJson as twHttpGetJson,
  httpPostJson as twHttpPostJson,
  httpPost as twHttpPost,
  httpPut as twHttpPut,
  httpPatch as twHttpPatch,
  httpDelete as twHttpDelete,
} from '@treatwell/ui';
import {v4 as uuidv4} from 'uuid';
import {ensureWafToken} from './wafToken';
import {
  WAF_DEBUG_HEADER,
  formatWafDebugHeader,
  hasWafToken,
} from './wafDebugHeader';
import {logTokenlessRequest} from './wafDebugLog';

type RequestParams = {path: string; options?: WretchOptions};

async function prepareWafRequest<P extends RequestParams>(
  params: P,
  method: string
): Promise<P> {
  const wafTokenState = await ensureWafToken();
  if (!wafTokenState || hasWafToken(wafTokenState)) return params;

  const ruid =
    typeof params.options?.headers?.ruid === 'string'
      ? params.options.headers.ruid
      : uuidv4();
  logTokenlessRequest(wafTokenState, {ruid, method, path: params.path});
  return {
    ...params,
    options: {
      ...params.options,
      headers: {
        ...params.options?.headers,
        ruid,
        [WAF_DEBUG_HEADER]: formatWafDebugHeader(wafTokenState),
      },
    },
  };
}

export async function httpGetJson<T>(
  params: Parameters<typeof twHttpGetJson>[0]
) {
  return twHttpGetJson<T>(await prepareWafRequest(params, 'GET'));
}

export async function httpPostJson<T>(
  params: Parameters<typeof twHttpPostJson>[0]
) {
  return twHttpPostJson<T>(await prepareWafRequest(params, 'POST'));
}

export async function httpPostRes<Result = WretchResponse>(
  params: Parameters<typeof twHttpPost>[0],
  cb?: (response: WretchResponse) => Result | Promise<Result>
) {
  return twHttpPost(await prepareWafRequest(params, 'POST')).res(cb);
}

export async function httpPutText(params: Parameters<typeof twHttpPut>[0]) {
  return twHttpPut(await prepareWafRequest(params, 'PUT')).text();
}

export async function httpPatchText(params: Parameters<typeof twHttpPatch>[0]) {
  return twHttpPatch(await prepareWafRequest(params, 'PATCH')).text();
}

export async function httpDeleteRes(
  params: Parameters<typeof twHttpDelete>[0]
) {
  return twHttpDelete(await prepareWafRequest(params, 'DELETE')).res();
}
